/**
 * devicePixelRatio-correct canvas sizing. The classic bug is conflating
 * backing-store pixels with CSS pixels — this keeps `canvas.width/height`
 * (the real pixel buffer) separate from `canvas.style.width/height` (layout
 * size), and applies a matching transform so every draw call after `resize()`
 * can be written in plain CSS-pixel units.
 */
export interface CanvasSurface {
  readonly canvas: HTMLCanvasElement;
  readonly ctx: CanvasRenderingContext2D;
  /** Current logical (CSS-pixel) size — the coordinate space draw calls should use. */
  size(): { width: number; height: number };
  destroy(): void;
}

/** Cap devicePixelRatio at 2 — an uncapped 3-4x DPR on some devices quietly quadruples the pixel buffer and tanks frame time, exactly the risk Canvas 2D (over WebGL) was chosen to avoid. */
const MAX_DPR = 2;

export function mountCanvasSurface(container: HTMLElement): CanvasSurface {
  const canvas = document.createElement('canvas');
  canvas.className = 'stack-tower-canvas';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context unavailable');

  let width = 1;
  let height = 1;

  function resize() {
    const rect = container.getBoundingClientRect();
    width = Math.max(1, rect.width);
    height = Math.max(1, rect.height);
    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  // ResizeObserver covers container-size changes beyond a plain viewport
  // resize (layout reflow, a sidebar opening) — but verified live that it
  // doesn't reliably fire for every real resize path (e.g. a device
  // rotation or a window resize while already mounted, in at least one
  // tested environment), so `window.resize` is a deliberate redundant
  // fallback, not dead code. Both call the same idempotent `resize()`.
  const observer = new ResizeObserver(resize);
  observer.observe(container);
  window.addEventListener('resize', resize);
  resize();

  return {
    canvas,
    ctx,
    size: () => ({ width, height }),
    destroy() {
      observer.disconnect();
      window.removeEventListener('resize', resize);
      canvas.remove();
    },
  };
}
